import React, { Component, ComponentType, ErrorInfo, ReactNode } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import * as Sentry from '@sentry/react';
import { withTranslation, WithTranslationProps } from 'hocs';
import Snackbar from '../Feedback/Snackbar';
import { errorStore } from '../../stores';

const DELAY_MS = 5000;

export interface ErrorBoundaryProps extends WithTranslationProps {
  children: ReactNode;
  fallbackComponent?: ComponentType | null;
  message?: string;
  errors?: string | null;
}

interface ErrorBoundaryState {
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private timeout: ReturnType<typeof setTimeout> | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      error: null,
    };
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { errors } = this.props;
    const { errors: prevErrors } = prevProps;

    if (errors && prevErrors !== errors) {
      this.timeout = setTimeout(() => {
        errorStore.setError('');
      }, DELAY_MS);
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error });

    Sentry.withScope((scope) => {
      scope.setLevel('fatal');
      scope.setExtra('errorInfo', errorInfo);
      scope.setExtra('componentStack', errorInfo?.componentStack);
      Sentry.captureException(error);
    });
  }

  componentWillUnmount() {
    if (this.timeout) clearTimeout(this.timeout);
    this.timeout = null;
  }

  handleReload = () => {
    window.location.reload();
  };

  renderActionButtons = () => {
    return (
      <IconButton key="close" aria-label="close" color="inherit" onClick={this.handleReload}>
        <RefreshIcon />
      </IconButton>
    );
  };

  render() {
    const {
      message,
      children,
      fallbackComponent: FallbackComponent,
      t,
      errors: storeError,
    } = this.props;
    const { error } = this.state;
    const defaultMessage = `${t('unrecoverableErrorOccured')}. ${t('tryToReloadPage')}`;
    const errorMessage = typeof message === 'undefined' ? defaultMessage : message;

    if (storeError) {
      return (
        <>
          {children}
          <Snackbar open message={storeError} variant="error" action={this.renderActionButtons()} />
        </>
      );
    }

    if (error) {
      if (FallbackComponent) {
        return <FallbackComponent />;
      }

      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            gap: 2,
            p: 4,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" color="error">
            {errorMessage}
          </Typography>
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={this.handleReload}>
            {t('reload')}
          </Button>
        </Box>
      );
    }

    return children;
  }
}

export default withTranslation(ErrorBoundary);
